/**
 * 🏛️ NonprofitsBreakdown — Iter 215 + Iter279 (Wave 1)
 *
 * Three horizontal cards stacked vertically — one per PHASE of the
 * Exchange Alliance pipeline: Triage → Sanctuary → Career.
 *
 * The Alliance establishes and holds the standard for all three
 * phases; credentialed partner agencies come alongside to administer
 * them. Each card deep-links to the public surface that carries that
 * phase's detail.
 */
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";

const PHASES = [
  {
    key:     "stabilization",
    phase:   "Phase 01 · Days 1 – 30",
    name:    "Triage & Stabilization",
    accent:  "#1B4332",
    tag:     "OUTREACH · INTAKE · BEHAVIORAL HEALTH",
    image:   "https://images.pexels.com/photos/7176026/pexels-photo-7176026.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt:     "A trauma-informed counselor leaning forward attentively, hands gently meeting in a moment of empathic listening — warm window light, no clipboard, no clinical sterility.",
    mission: "We go to the Lost where they are — encampment outreach, Vibe Check intake, and a behavioral-health baseline that turns survival mode into an actual plan.",
    bullets: [
      "Encampment outreach and Vibe Check intake",
      "Mental-health, behavioral and medical baseline",
      "Bridge Pipeline activates Big Four ID recovery",
    ],
    to:      "/intake",
    cta:     "Start an Intake",
  },
  {
    key:     "sanctuary",
    phase:   "Phase 02 · Months 2 – 12",
    name:    "Residential Sanctuary",
    accent:  "#C9A227",
    tag:     "STABILIZATION · COMMUNITY · HOUSING",
    image:   "https://images.unsplash.com/photo-1609253932702-796cbf3d3171?auto=format&fit=crop&w=1200&q=80",
    alt:     "A diverse community group seated together in a sunlit common area, mid-discussion, supporting each other.",
    mission: "Where the Least are made whole — sober transitional housing, daily structure, and a community that holds the line between crisis and career.",
    bullets: [
      "159 sober transitional beds across vetted partner homes",
      "Daily accountability check-ins + curfewed compliance ladder",
      "Step-down pathway into the independent-housing roster",
    ],
    to:      "/four-pillars",
    cta:     "See Our Foundation",
  },
  {
    key:     "career",
    phase:   "Phase 03 · Months 13 – 24",
    name:    "Career & Independence",
    accent:  "#0F766E",
    tag:     "PLACEMENT · CAREER · INDEPENDENCE",
    image:   "https://images.unsplash.com/photo-1758691737246-95bf8f09a997?auto=format&fit=crop&w=1200&q=80",
    alt:     "Office workers collaborating candidly around a shared screen in a modern workplace — focused, engaged, productive.",
    mission: "Where the Last are first in line — paid placement with one of 250+ partner employers, a real paycheck, and a path to homeownership.",
    bullets: [
      "Direct placement with pre-certified employer roster",
      "Inline payroll + compliance via our Personal Advancement and Career Training",
      "Phase-III homeownership pathway for residents who make the run",
    ],
    to:      "/employers",
    cta:     "Meet Our Employers",
  },
];

export default function NonprofitsBreakdown() {
  return (
    <section
      id="alliance"
      className="bg-[#FAFAF7] py-24 lg:py-28 border-t border-[#0A0612]/5"
      data-testid="nonprofits-breakdown"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        {/* Section header */}
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-6 bg-[#1B4332]" />
              Three Phases · One Mandate
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0612] leading-tight">
            Each phase has a name, a standard,{" "}
            <span className="text-[#1B4332]">and someone accountable.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#0A0612]/60 leading-relaxed">
            We establish all three phases and hold the standard for each one.
            Credentialed partner agencies come alongside us to administer them —
            so the person the world left for last moves through one continuous
            journey instead of starting over at every door.
          </p>
        </div>

        {/* Stacked cards — whole article is a router link into that phase. */}
        <div className="grid grid-cols-1 gap-10 lg:gap-14">
          {PHASES.map((np, idx) => (
            <Link
              key={np.key}
              to={np.to}
              data-testid={`nonprofit-card-${np.key}`}
              aria-label={`${np.name} — ${np.cta}`}
              className="
                group block cursor-pointer
                grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center
                bg-white rounded-3xl p-4 md:p-6 lg:p-8
                border border-[#0A0612]/5
                shadow-sm hover:shadow-2xl
                transition-all duration-200 ease-out
                hover:-translate-y-1 hover:border-[#0A0612]/20
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B4332] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAFAF7]
              "
              style={{ textDecoration: "none" }}
            >
              {/* Image — alternates side on desktop for visual rhythm. */}
              <div
                className={
                  "relative lg:col-span-5 aspect-[4/3] rounded-2xl overflow-hidden bg-[#F3F3EE] " +
                  (idx % 2 === 1 ? "lg:order-2" : "")
                }
              >
                <img
                  src={np.image}
                  alt={np.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Phase number plate */}
                <span
                  data-testid={`nonprofit-phase-plate-${np.key}`}
                  className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-[#0A0612]/10 shadow-md font-mono text-[10px] tracking-[0.22em] uppercase font-bold"
                  style={{ color: np.accent }}
                >
                  {np.phase}
                </span>
              </div>

              {/* Content */}
              <div
                className={
                  "lg:col-span-7 flex flex-col gap-5 " +
                  (idx % 2 === 1 ? "lg:order-1 lg:pr-4" : "lg:pl-4")
                }
              >
                {/* Tag with accent color */}
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: np.accent }} />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: np.accent }}>
                    {np.tag}
                  </span>
                </div>

                {/* Name */}
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0612] leading-tight">
                  {np.name}
                </h3>

                {/* Mission line */}
                <p className="text-base md:text-lg text-[#0A0612]/75 leading-relaxed">
                  {np.mission}
                </p>

                {/* Bullets */}
                <ul className="flex flex-col gap-2.5 mt-1">
                  {np.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm md:text-base text-[#0A0612]/70 leading-relaxed"
                    >
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ background: np.accent }}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {/* Visual CTA affordance (not a nested link — the whole
                    card is the link. This is styled as a span so the
                    outer <Link> semantics stay clean. */}
                <span
                  data-testid={`nonprofit-cta-${np.key}`}
                  className="
                    mt-3 inline-flex items-center gap-2
                    text-[#1B4332] font-semibold text-sm tracking-tight
                    transition-all group-hover:gap-3
                  "
                >
                  {np.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Charter footer — names the anchor non-profit + its official site */}
        <div className="mt-12 pt-8 border-t border-[#0A0612]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-[#0A0612]/60 leading-relaxed max-w-2xl">
            Chartered and operated by{" "}
            <strong className="text-[#0A0612]">The Life Foundation</strong>, holder of
            the operating charter, the 159-unit residential community, and the
            fiduciary obligations to residents and funders.
          </p>
          <a
            href="https://lifefoundationrebuild.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="charter-holder-link"
            className="shrink-0 inline-flex items-center gap-2 font-semibold text-sm text-[#1B4332] hover:gap-3 transition-all"
          >
            Visit The Life Foundation
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
